import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';

/* ===============================
 * MODELOS
 * =============================== */

export interface Subnotification {
  id: number;
  user_id: number;
  is_read: boolean;
  read_at?: string | null;
  target_type: 'USER' | 'GROUP';

  notification: {
    id: number;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    priority: 'low' | 'medium' | 'high';
    link_url?: string | null;
    created_at: string;
  };
}

export interface NotificationGroup {
  id: number;
  code: string;
  name: string;
  description?: string;
}

export interface NotificationGroupUser {
  id: number;
  group_id: number;
  user_id: number;
  user?: {
    id: number;
    name: string;
  };
}

export interface NotificationEvent {
  id: number;
  event_code: string;
  description?: string;
}

export interface PaginatedNotifications {
  page: number;
  limit: number;
  count: number;
  data: Subnotification[];
}
export interface NotificationFilters {
  message?: string;
  priority?: 'low' | 'medium' | 'high';
  read?: boolean;
  fromDate?: string;
  toDate?: string;
}

/* ===============================
 * SERVICE
 * =============================== */

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private baseUrl = `${environment.apiUrl}/productos/registro_requerimiento/notifications`;

  private notifications$ = new BehaviorSubject<Subnotification[]>([]);
  private previewNotifications$ = new BehaviorSubject<Subnotification[]>([]);
  constructor(private http: HttpClient) { }

  /* =====================================================
   * 📬 NOTIFICACIONES (USUARIO)
   * ===================================================== */

  init(page: number = 1, limit: number = 10): void {
    this.getMyNotifications(page, limit).subscribe((res) => {
      this.notifications$.next(res.data);
    });
  }
  /**
   * 🔔 Preview para campana (SIN paginación)
   * @param filtro 'todas' | 'no_leidas'
   */
  getNotificationsPreview(
    filtro: 'todas' | 'no_leidas' = 'todas',
  ): Observable<Subnotification[]> {
    return this.http.get<Subnotification[]>(
      `${this.baseUrl}/preview?filtro=${filtro}`,
    );
  }

  getMyNotifications(
    page: number = 1,
    limit: number = 10,
    filters?: NotificationFilters,
  ): Observable<PaginatedNotifications> {
    let params = `page=${page}&limit=${limit}`;

    if (filters) {
      if (filters.message) {
        params += `&message=${encodeURIComponent(filters.message)}`;
      }

      if (filters.priority) {
        params += `&priority=${filters.priority}`;
      }

      if (filters.read !== undefined) {
        params += `&read=${filters.read}`;
      }

      if (filters.fromDate) {
        params += `&fromDate=${filters.fromDate}`;
      }

      if (filters.toDate) {
        params += `&toDate=${filters.toDate}`;
      }
    }

    return this.http.get<PaginatedNotifications>(`${this.baseUrl}?${params}`);
  }

  markAsRead(subnotificationId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/${subnotificationId}/read`, {});
  }

  getNotificationsStream(): Observable<Subnotification[]> {
    return this.notifications$.asObservable();
  }

  addNotificationRealtime(sub: Subnotification) {
    const current = this.notifications$.getValue();
    this.notifications$.next([sub, ...current]);
  }

  /* =====================================================
   *    GRUPOS
   * ===================================================== */

  getGroups(): Observable<NotificationGroup[]> {
    return this.http.get<NotificationGroup[]>(`${this.baseUrl}/groups`);
  }
  listGroups(params?: any): Observable<any> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }

    return this.http.get(`${this.baseUrl}/groupsPaginated`, { params: httpParams });
  }
  createGroup(payload: {
    code: string;
    name: string;
    description?: string;
  }): Observable<NotificationGroup> {
    return this.http.post<NotificationGroup>(`${this.baseUrl}/groups`, payload);
  }

  updateGroup(
    groupId: number,
    payload: Partial<NotificationGroup>,
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/groups/${groupId}`, payload);
  }

  deleteGroup(groupId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/groups/${groupId}`);
  }

  /* =====================================================
   *     USUARIOS EN GRUPO
   * ===================================================== */

  getGroupUsers(groupId: number): Observable<NotificationGroupUser[]> {
    return this.http.get<NotificationGroupUser[]>(
      `${this.baseUrl}/groups/${groupId}/users`,
    );
  }

  addUsersToGroup(groupId: number, userIds: number[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/groups/${groupId}/users`, {
      user_ids: userIds,
    });
  }

  removeUserFromGroup(groupId: number, userId: number): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/groups/${groupId}/users/${userId}`,
    );
  }

  /* =====================================================
   * ⚡ EVENTOS
   * ===================================================== */

  getEvents(): Observable<NotificationEvent[]> {
    return this.http.get<NotificationEvent[]>(`${this.baseUrl}/events`);
  }
  listEvents(params?: any): Observable<any> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }

    return this.http.get(`${this.baseUrl}/eventsPaginated`, { params: httpParams });
  }

  createEvent(payload: {
    event_code: string;
    description?: string;
  }): Observable<NotificationEvent> {
    return this.http.post<NotificationEvent>(`${this.baseUrl}/events`, payload);
  }

  updateEvent(
    id: number,
    payload: Partial<NotificationEvent>,
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/events/${id}`, payload);
  }

  deleteEvent(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/events/${id}`);
  }

  /* =====================================================
   * 🔔 PREVIEW STREAM (CAMPANA)
   * ===================================================== */

  getPreviewStream(): Observable<Subnotification[]> {
    return this.previewNotifications$.asObservable();
  }

  loadPreview(filtro: 'todas' | 'no_leidas' = 'todas') {
    this.getNotificationsPreview(filtro).subscribe((rows) => {
      this.previewNotifications$.next(rows);
    });
  }

  addPreviewNotificationRealtime(sub: Subnotification) {
    const current = this.previewNotifications$.getValue();

    // Evitar duplicados
    if (current.some((n) => n.id === sub.id)) return;

    this.previewNotifications$.next([sub, ...current]);
  }

  setPreviewFilter(filtro: 'todas' | 'no_leidas') {
    this.loadPreview(filtro);
  }
  markAllPreviewAsRead(): Observable<any> {
    return this.http.post(`${this.baseUrl}/preview/read-all`, {});
  }
  clearUnreadPreviewLocal() {
    const updated = this.previewNotifications$
      .getValue()
      .map((n) => ({ ...n, is_read: true }));

    this.previewNotifications$.next(updated);
  }
  /* =====================================================
   * ⚡ EMITIR NOTIFICACIÓN (USUARIO / ADMIN)
   * ===================================================== */
  emitNotification(payload: {
    message: string;
    users_ids?: number[];
    groups_ids?: number[];
    event_id: number;
    type?: 'info' | 'success' | 'warning' | 'error';
    priority?: 'low' | 'medium' | 'high';
    link_url?: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/emit`, payload);
  }
}
